<template>
  <TabbedBox :useQueryNavigation="true">
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-activepanel-list">
        <i class="far fa-tasks"></i> Panel Work In Progress List
      </Tab>
      <Tab tabid="tab-stagetask-list">
        <i class="far fa-tasks"></i> Panel Stage Task List
      </Tab>
      <Tab tabid="tab-station-performance-graphs">
        <i class="far fa-chart-line"></i> Station Performance Graphs
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-activepanel-list">
        <DataTable :columns="panelColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="5" :url="panelDataTableURL" :filterOptions="panelFilterOptions" :displayTools="true" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Production Panels">
          <template v-slot:title>
            <ContentTitle>Panel Work In Progress List</ContentTitle>
            <ContentSubTitle>List of all production panels that are pending production or currently in production</ContentSubTitle>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkProductionPanel :id="row.data.id" :name="row.data.barcode" :newTab="false" />
              </td>
              <td>
                <LabelPanelStatusBadge :status="row.data.status" />
              </td>
              <td>
                {{row.data.panelHeight}} x {{row.data.panelWidth}}
              </td>
              <td>
                {{formatDecimal(row.data.panelArea, 2)}}m&sup2;
              </td>
              <td>
                {{row.data.orderItem.stock.name}}
              </td>              
              <td>
                <LinkOrder :id="row.data.order.id" :name="row.data.order.sequenceNumber" />
              </td>
              <td>
                {{row.data.currentProductionStage ? row.data.currentProductionStage.name : '-'}}
              </td>
              <td>
                {{row.data.createdAt | formatDateTime}}
              </td>
              <td>
                {{row.data.dateProductionDue | formatDateTime | formatHyphenEmptyValue}}
              </td>
            </tr>
          </template>
          <template v-slot:summary="summary">
          <tr key="summary">
            <td>Totals</td>
            <td></td>
            <td></td>
            <td>{{formatDecimal(summary.data.totalPanelArea, 2)}}m&sup2;</td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
            <td></td>
          </tr>
        </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-stagetask-list">
        <DataTable :columns="stageTaskColumnList" defaultColumnOrderDir="DESC" :defaultColumnOrderIndex="0" :url="stageTaskDataTableURL" :filterOptions="stageTaskFilterOptions" :displayTools="true" :hasDateRangeFilter="true" :displayExportTools="true" exportTitle="Production Panel Stage Tasks">
          <template v-slot:title>
            <ContentTitle>Production Panel Stage List</ContentTitle>
            <ContentSubTitle>List of all production panel stage tasks, linking panels to required production stages</ContentSubTitle>
          </template>
          <template v-slot:row="row">
            <tr :key="row.data.id">
              <td class="table-data-nav-link">
                <LinkProductionPanel :id="row.data.productionPanel.id" :name="row.data.productionPanel.barcode" />
              </td>
              <td>
                <LinkProductionStage :id="row.data.productionStage.id" :name="row.data.productionStage.name" />
              </td>
              <td>
                <LabelStageTaskStatusBadge :status="row.data.status" />
              </td>
              <td>
                {{row.data.productionPanel.panelHeight}} x {{row.data.productionPanel.panelWidth}}
              </td>
              <td>
                <LinkStock :id="row.data.productionPanel.stock.id" :name="row.data.productionPanel.stock.name" />
              </td>
              <td>
                <LinkOrder :id="row.data.productionPanel.order.id" :name="row.data.productionPanel.order.sequenceNumber" />
              </td>
              <td>
                {{formatDate(row.data.productionPanel.order.dateCurrentlyDue)}}
              </td>
              <td>
                {{formatDateTime(row.data.startedDate)}}
              </td>
              <td>
                {{formatDateTime(row.data.dueDate)}}
              </td>
              <td>
                {{formatDateTime(row.data.completionDate)}}
              </td>
              <td>
                <LinkProductionStation v-if="row.data.startedProductionStation" :id="row.data.startedProductionStation.id" :name="row.data.startedProductionStation.name" />
              </td>
              <td>
                <LinkProductionStation v-if="row.data.completedProductionStation" :id="row.data.completedProductionStation.id" :name="row.data.completedProductionStation.name" />
              </td>
              
            </tr>
          </template>
        </DataTable>
      </TabPage>

      <TabPage tabid="tab-station-performance-graphs">
        <div class="row no-gutter">
          <div class="col-lg-6">
            <ContentTitle>Station Performance For Today</ContentTitle>
          </div>
        </div>
        <div class="row no-gutter">
          <div class="col-12">

            <div class="station-performance-graph-container row no-gutters" v-if="!loading">
              <template v-for="station of stationList">
                <div class="graph-container col-3 float-left" :key="station.id" v-if="station.showStationGraph">
                  <h2 class="graph-title text-truncate">{{stationStageName(station)}} - {{station.name}}</h2>
                  <StationPerformanceGraph ref="stationGraph" :productionStation="station" />
                </div>
              </template>
            </div>
          </div>
        </div>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<style scoped>
.graph-title {
  font-size: 18px;
  margin-top: 0px;
  margin-bottom: 5px;
}
.graph-container {
  padding: 10px;
  margin-bottom: 8px;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import buildUrl from 'build-url'
import { mapState } from 'vuex'

import Utils from '@/utils/utils'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportContactSelect from '@/components/reporting/reportContactSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'

import LabelStageTaskStatusBadge from '@/modules/production/components/labelStageTaskStatusBadge'
import LabelPanelStatusBadge from '@/modules/production/components/labelPanelStatusBadge'
import StationPerformanceGraph from '@/apps/glass-production-station/src/components/stationPerformanceGraph'
import ProductionReportPanelListPopover from '@/modules/intelligence/components/productionReportPanelListPopover'

import { ProductionPanelStatusTypes, ProductionPanelStatusTypeLookup, ProductionPanelStageTaskStatusTypes, ProductionPanelStageTaskStatusTypeLookup } from '@/store/types'

export default {
  name: 'ProductionPerformance',
  components: {
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportContactSelect,
    ReportFilterGroup,
    LabelStageTaskStatusBadge,
    LabelPanelStatusBadge,
    StationPerformanceGraph,
    ProductionReportPanelListPopover
  },
  data() {
    return {
      loading: false,
      displayValueMode: 0,
      performanceSummary: {},
      stationList: [],

      defaultSummaryFilter: {    
        startDate: moment().local().startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        contactId: null,
        contactName: '',
      },
      editingSummaryFilter: {},
      appliedSummaryFilter: {},

      stageTaskColumnList: [
        { title: "Panel", data: "productionPanelId" },
        { title: "Stage", data: "productionStage.name" },
        { title: "Stage Status", data: "status" },
        { title: "Panel Size", data: "productionPanel.panelSize", sortable: false },
        { title: "Panel Stock", data: "productionPanel.stock.name" },
        { title: "Order", data: "productionPanel.order.sequenceNumber" },
        { title: "Order Due Date", data: "$productionPanel.order.dateCurrentlyDue$", dateFilter: true },
        { title: "Started Date", data: "startedDate", type: 'datetime', dateFilter: true },
        { title: "Stage Due Date", data: "dueDate", type: 'datetime', dateFilter: true },
        { title: "Stage Completed Date", data: "completionDate", type: 'datetime', dateFilter: true },
        { title: "Started Station", data: "startedProductionStation.name" },
        { title: "Completed Station", data: "completedProductionStation.name" },
        
      ],
      stageTaskFilterOptions: [
        {
          field: 'status',
          name: 'Status',
          options: [
            { name: Utils.getLookupName(ProductionPanelStageTaskStatusTypeLookup, ProductionPanelStageTaskStatusTypes.pending), value: ProductionPanelStageTaskStatusTypes.pending, class: 'label-production-panel-status-pending' },
            { name: Utils.getLookupName(ProductionPanelStageTaskStatusTypeLookup, ProductionPanelStageTaskStatusTypes.production), value: ProductionPanelStageTaskStatusTypes.production, class: 'label-production-panel-status-production' },
            { name: Utils.getLookupName(ProductionPanelStageTaskStatusTypeLookup, ProductionPanelStageTaskStatusTypes.completed), value: ProductionPanelStageTaskStatusTypes.completed, class: 'label-production-panel-status-completed' },
            { name: Utils.getLookupName(ProductionPanelStageTaskStatusTypeLookup, ProductionPanelStageTaskStatusTypes.cancelled), value: ProductionPanelStageTaskStatusTypes.cancelled, class: 'label-production-panel-status-cancelled' }
          ]
        },
      ],
      panelFilterOptions: [
        {
          field: 'status',
          name: 'Status',
          options: [
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.pending), value: ProductionPanelStatusTypes.pending, class: 'label-production-panel-status-pending' },
            { name: Utils.getLookupName(ProductionPanelStatusTypeLookup, ProductionPanelStatusTypes.production), value: ProductionPanelStatusTypes.production, class: 'label-production-panel-status-production' },
          ]
        },
      ],
      panelColumnList: [
        { title: "Panel", data: "id" },
        { title: "Status", data: "status" },
        { title: "Size", data: "panelSize", sortable: false },
        { title: "Area", data: "panelArea" },
        { title: "Stock", data: "orderItem.stock.name" },
        { title: "Order", data: "order.sequenceNumber" },
        { title: "Current Stage", data: "currentProductionStage.name" },
        { title: "Creation Date", data: "createdAt", dateFilter: true, type: 'datetime' },
        { title: "Production Due Date", data: "dateProductionDue", dateFilter: true, type: 'datetime' },
      ]
    }
  },
  computed: {
    totalNormalPanelCount() {
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.normalStats.totalPanels, 0)
    },
    totalNormalDaysBehind() {
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.normalStats.totalDaysBehind, 0)
    },
    totalNormalPanelsOnSchedule() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.normalStats.totalPanelsOnSchedule, 0)
    },
    totalNormalPanelsAhead() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.normalStats.totalPanelsAhead, 0)
    },
    totalNormalPanelsBehind() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.normalStats.totalPanelsBehind, 0)
    },
    totalEarlierPanelCount() {
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.requestedEarlierStats.totalPanels, 0)
    },
    totalEarlierDaysBehind() {
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.requestedEarlierStats.totalDaysBehind, 0)
    },
    totalEarlierPanelsOnSchedule() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.requestedEarlierStats.totalPanelsOnSchedule, 0)
    },
    totalEarlierPanelsAhead() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.requestedEarlierStats.totalPanelsAhead, 0)
    },
    totalEarlierPanelsBehind() {
      if(this.displayValueMode != 0)
        return ''
      if(!this.performanceSummary.stageTaskSummaryList)
        return 0
      return this.performanceSummary.stageTaskSummaryList.reduce((curValue, stage) => curValue + stage.requestedEarlierStats.totalPanelsBehind, 0)
    },

    stageTaskDataTableURL() {
      var params = {
        filter: {}
      }
      return '/intelligence/production/performance/detailedlist?' + $.param(params)
    },
    panelDataTableURL() {
      var params = {
        filter: {
        },
        includesummary: true
      }
      return '/intelligence/production/panels/wiplist?' + $.param(params)
    },
  },
  mounted() {
    this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
    this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

    this.requestSummary()
    this.requestProductionList()
  },
  beforeDestroy() {
  },
  methods: {
    requestSummary() {
      let startDate = moment(this.appliedSummaryFilter.startDate).local().startOf('day').toDate().toISOString()
      let endDate = moment(this.appliedSummaryFilter.endDate).local().endOf('day').toDate().toISOString()

      let filterData = {
        startDate: startDate,
        endDate: endDate,
        contactId: this.appliedSummaryFilter.contactId,
        includePanels: true
      }

      this.loading = true
      this.$apiService.analyticsService.getProductionPerformanceSummary(filterData)
      .then(response => {
        this.performanceSummary = response.data
      })
      .finally(() => {
        this.loading = false
      })
    },
    requestProductionList() {
      this.$apiService.productionService.getProductionStationList()
      .then(response => {
        this.stationList = response.data
      })
      .finally(() => {
      })
    },
    displayDaysBehindAvg(average) {
      var displayClass = ''
      var symbol = ''

      if(average > 0) {
        displayClass = 'stats-bad'
        symbol = '+'
      } else if(average < 0) {
        average = -average
        displayClass = 'stats-good'
        symbol = '-'
      }

      var outputAvg = this.$options.filters.formatDecimal(average, 2, true)
      return `<span class="${displayClass}">${symbol}${outputAvg}</span>`
    },
    displayDaysBehind(days) {
      let outputDays = this.$options.filters.formatInteger(days, false, true)
      if(days > 0)
        return `+${outputDays}`
      else if(days < 0)
        return `-${outputDays}`
      return outputDays
    },
    displayPanelCount(panelCount, totalPanelCount) {
      if(this.displayValueMode == 0)
        return this.$options.filters.formatInteger(panelCount, false, true)

      var percent = 0
      if(totalPanelCount > 0)
        percent = panelCount / totalPanelCount * 100.0

      return this.$options.filters.formatPercent(percent)
    },
    stationStageName(station) {
      if(station.productionStages && station.productionStages.length > 0) {
        return station.productionStages[0].name
      }
      return 'None'
    },
    displayPanelListPopover(evt, stage, panelList, title) {
      this.$refs.panelListPopover.displayFromElement(evt.target, stage, panelList, title)
    },
    applySummaryFilter() {
      this.appliedSummaryFilter = _.clone(this.editingSummaryFilter)

      this.requestSummary()
    },
    resetSummaryFilter() {
      this.editingSummaryFilter = _.clone(this.defaultSummaryFilter)
      this.appliedSummaryFilter = _.clone(this.defaultSummaryFilter)

      this.requestSummary()
    }

  }
}
</script>
